import { NavLink, Routes, Route, Navigate } from 'react-router-dom';
import { Settings as SettingsIcon, GitBranch, XCircle, HelpCircle, Globe, Upload, Plug, Briefcase, Target, Bot, Mail, KeyRound, SlidersHorizontal, Clock, Share2, Send, Bell } from 'lucide-react';
import { cn } from '@/lib/utils';
import PipelineSettings from './settings/PipelineSettings';
import CloseReasonSettings from './settings/CloseReasonSettings';
import QualifyingQuestionSettings from './settings/QualifyingQuestionSettings';
import DealTypeSettings from './settings/DealTypeSettings';
import IcpSettings from './settings/IcpSettings';
import AiSdrSettings from './settings/AiSdrSettings';
import EmailTemplateSettings from './settings/EmailTemplateSettings';
import SendTemplateSettings from './settings/SendTemplateSettings';
import NotificationSettings from './settings/NotificationSettings';
import WebhookSettings from './settings/WebhookSettings';
import HubspotImportSettings from './settings/HubspotImportSettings';
import IntegrationsSettings from './settings/IntegrationsSettings';
import ApiKeySettings from './settings/ApiKeySettings';
import CustomFieldSettings from './settings/CustomFieldSettings';
import SequenceSchedulingSettings from './settings/SequenceSchedulingSettings';
import SocialChannelSettings from './settings/SocialChannelSettings';

const settingsGroups = [
  {
    label: 'Pipeline',
    items: [
      { to: '/admin/settings/pipeline', label: 'Pipeline', icon: GitBranch },
      { to: '/admin/settings/close-reasons', label: 'Close Reasons', icon: XCircle },
      { to: '/admin/settings/project-types', label: 'Project Types', icon: Briefcase },
      { to: '/admin/settings/custom-fields', label: 'Custom Fields', icon: SlidersHorizontal },
    ],
  },
  {
    label: 'Qualification',
    items: [
      { to: '/admin/settings/qualifying-questions', label: 'Qualifying Questions', icon: HelpCircle },
      { to: '/admin/settings/icp', label: 'Ideal Customer Profile', icon: Target },
      { to: '/admin/settings/ai-sdr', label: 'AI SDR', icon: Bot },
    ],
  },
  {
    label: 'Outreach',
    items: [
      { to: '/admin/settings/send-templates', label: 'Email Templates', icon: Send },
      { to: '/admin/settings/sequence-scheduling', label: 'Sequence Scheduling', icon: Clock },
      { to: '/admin/settings/social-channels', label: 'Social Channels', icon: Share2 },
    ],
  },
  {
    label: 'Integrations',
    items: [
      { to: '/admin/settings/webhooks', label: 'Webhooks', icon: Globe },
      { to: '/admin/settings/hubspot-import', label: 'HubSpot Import', icon: Upload },
      { to: '/admin/settings/integrations', label: 'Integrations', icon: Plug },
      { to: '/admin/settings/api-keys', label: 'API Keys', icon: KeyRound },
    ],
  },
  {
    label: 'Account',
    items: [
      { to: '/admin/settings/notifications', label: 'Notifications', icon: Bell },
      { to: '/admin/settings/email-templates', label: 'System Templates', icon: Mail },
    ],
  },
];

export default function Settings() {
  return (
    <div className="page-enter">
      <div className="mb-6">
        <h1 className="text-heading-1 text-secondary flex items-center gap-3">
          <SettingsIcon className="w-7 h-7 text-grey-400" />
          Settings
        </h1>
        <p className="text-body-sm text-grey-500 mt-1">Manage your sales pipeline configuration.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Settings nav */}
        <div className="space-y-5">
          {settingsGroups.map((group) => (
            <div key={group.label}>
              <p className="px-4 mb-1 text-[11px] font-semibold uppercase tracking-wider text-grey-400">
                {group.label}
              </p>
              <div className="space-y-1">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary/5 text-primary'
                          : 'text-grey-600 hover:bg-grey-100 hover:text-secondary'
                      )
                    }
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Settings content */}
        <div className="lg:col-span-3">
          <Routes>
            <Route path="pipeline" element={<PipelineSettings />} />
            <Route path="close-reasons" element={<CloseReasonSettings />} />
            <Route path="project-types" element={<DealTypeSettings />} />
            <Route path="qualifying-questions" element={<QualifyingQuestionSettings />} />
            <Route path="icp" element={<IcpSettings />} />
            <Route path="ai-sdr" element={<AiSdrSettings />} />
            <Route path="email-templates" element={<EmailTemplateSettings />} />
            <Route path="send-templates" element={<SendTemplateSettings />} />
            <Route path="notifications" element={<NotificationSettings />} />
            <Route path="sequence-scheduling" element={<SequenceSchedulingSettings />} />
            <Route path="social-channels" element={<SocialChannelSettings />} />
            <Route path="webhooks" element={<WebhookSettings />} />
            <Route path="hubspot-import" element={<HubspotImportSettings />} />
            <Route path="custom-fields" element={<CustomFieldSettings />} />
            <Route path="integrations" element={<IntegrationsSettings />} />
            <Route path="api-keys" element={<ApiKeySettings />} />
            <Route path="*" element={<Navigate to="/admin/settings/pipeline" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
